import React, { useState, useEffect, useRef } from 'react';
import { CloseIcon, ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { FaExpand, FaCompress, FaDownload } from 'react-icons/fa';
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  ModalCloseButton,
  Image,
  Box,
  IconButton,
  Text,
  HStack,
} from '@chakra-ui/react';

const ShowImgPopup = ({ isImageModal, onClose, modalImageUrl, images = [] }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (modalImageUrl && images.length > 0) {
      const idx = images.indexOf(modalImageUrl);
      setCurrentIndex(idx !== -1 ? idx : 0);
    } else {
      setCurrentIndex(0);
    }
  }, [modalImageUrl, images]);

  const handlePrev = () => {
    if (images.length > 1) {
      setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    }
  };

  const handleNext = () => {
    if (images.length > 1) {
      setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    }
  };

  const toggleFullscreen = () => {
    if (!containerRef.current) return;

    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch((err) => {
        console.error('Error attempting to enable fullscreen:', err);
      });
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      }).catch((err) => {
        console.error('Error attempting to exit fullscreen:', err);
      });
    }
  };

  const handleDownload = async () => {
    const activeUrl = images.length > 0 ? images[currentIndex] : modalImageUrl;
    if (!activeUrl) return;

    try {
      const response = await fetch(activeUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const filename = activeUrl.split('/').pop() || 'download.jpg';
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // Fallback: Open in new tab if fetch fails due to CORS
      window.open(activeUrl, '_blank');
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isImageModal) return;
      if (e.key === 'ArrowLeft') {
        handlePrev();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      } else if (e.key === 'Escape') {
        if (document.fullscreenElement) {
          document.exitFullscreen();
        } else {
          onClose();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isImageModal, images, currentIndex]);

  const activeUrl = images.length > 0 ? images[currentIndex] : modalImageUrl;

  return (
    <Modal isOpen={isImageModal} onClose={onClose} isCentered size="full">
      <ModalOverlay bg="blackAlpha.700" />
      <ModalContent
        backgroundColor="#FFFFFF"
        padding="24px 8px 16px 8px"
        // borderRadius="16px"
        position="relative"
        overflow="hidden"
      >
        {!isFullscreen && (
          <HStack position="absolute" top="10px" right="50px" spacing={2} zIndex="modal">
            <IconButton
              aria-label="Download image"
              icon={<FaDownload />}
              borderRadius="full"
              bg="gray.100"
              color="gray.700"
              _hover={{ bg: 'gray.200' }}
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleDownload();
              }}
            />
            <IconButton
              aria-label="Toggle fullscreen"
              icon={<FaExpand />}
              borderRadius="full"
              bg="gray.100"
              color="gray.700"
              _hover={{ bg: 'gray.200' }}
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                toggleFullscreen();
              }}
            />
          </HStack>
        )}
        <ModalCloseButton zIndex="modal" />
        <ModalBody p={0} display="flex" flexDirection="column" alignItems="center" justifyContent={'center'}>
          <Box
            ref={containerRef}
            position="relative"
            width="100%"
            display="flex"
            justifyContent="center"
            alignItems="center"
            /*
            minH={isFullscreen ? '100vh' : '300px'}
            maxH={isFullscreen ? '100vh' : '70vh'}
            */
            flex={1}
            bg={isFullscreen ? 'black' : 'transparent'}
            p={isFullscreen ? 8 : 0}
          >
            {/* Top controls (download, fullscreen, exit fullscreen) */}
            {/* <HStack position="absolute" top="12px" right="12px" spacing={2} zIndex="3">
              <IconButton
                aria-label="Download image"
                icon={<FaDownload />}
                borderRadius="full"
                colorScheme="blackAlpha"
                bg="blackAlpha.600"
                color="white"
                _hover={{ bg: 'blackAlpha.800' }}
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownload();
                }}
              />
              <IconButton
                aria-label="Toggle fullscreen"
                icon={isFullscreen ? <FaCompress /> : <FaExpand />}
                borderRadius="full"
                colorScheme="blackAlpha"
                bg="blackAlpha.600"
                color="white"
                _hover={{ bg: 'blackAlpha.800' }}
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFullscreen();
                }}
              />
              {isFullscreen && (
                <IconButton
                  aria-label="Close fullscreen"
                  icon={<CloseIcon />}
                  borderRadius="full"
                  colorScheme="blackAlpha"
                  bg="blackAlpha.600"
                  color="white"
                  _hover={{ bg: 'blackAlpha.800' }}
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFullscreen();
                  }}
                />
              )}
            </HStack> */}
            {isFullscreen && (
              <HStack position="absolute" top="12px" right="12px" spacing={2} zIndex="3">
                <IconButton
                  aria-label="Download image"
                  icon={<FaDownload />}
                  borderRadius="full"
                  colorScheme="blackAlpha"
                  bg="blackAlpha.600"
                  color="white"
                  _hover={{ bg: 'blackAlpha.800' }}
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownload();
                  }}
                />
                <IconButton
                  aria-label="Toggle fullscreen"
                  icon={<FaCompress />}
                  borderRadius="full"
                  colorScheme="blackAlpha"
                  bg="blackAlpha.600"
                  color="white"
                  _hover={{ bg: 'blackAlpha.800' }}
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFullscreen();
                  }}
                />
                <IconButton
                  aria-label="Close fullscreen"
                  icon={<CloseIcon />}
                  borderRadius="full"
                  colorScheme="blackAlpha"
                  bg="blackAlpha.600"
                  color="white"
                  _hover={{ bg: 'blackAlpha.800' }}
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFullscreen();
                  }}
                />
              </HStack>
            )}

            {/* Left navigation arrow */}
            {images.length > 1 && (
              <IconButton
                aria-label="Previous image"
                icon={<ChevronLeftIcon w={8} h={8} />}
                position="absolute"
                left="8px"
                zIndex="2"
                onClick={(e) => {
                  e.stopPropagation();
                  handlePrev();
                }}
                borderRadius="full"
                colorScheme="blackAlpha"
                bg="blackAlpha.600"
                color="white"
                _hover={{ bg: 'blackAlpha.800' }}
              />
            )}

            {/* Main image */}
            {/* {activeUrl && (
              <Image
                src={activeUrl}
                alt="Preview"
                borderRadius={isFullscreen ? 'none' : 'lg'}
                maxW="100%"
                maxH={isFullscreen ? '100vh' : '70vh'}
                objectFit="contain"
              />
            )} */}
            {activeUrl && (
              <Image
                src={activeUrl}
                alt="Preview"
                borderRadius={isFullscreen ? 'none' : 'lg'}
                maxW="100%"
                h="86vh"
                maxH={isFullscreen ? (images && images.length > 1 ? 'calc(100vh - 80px)' : '100vh') : '90vh'}
                objectFit="contain"
              />
            )}

            {/* Right navigation arrow */}
            {images.length > 1 && (
              <IconButton
                aria-label="Next image"
                icon={<ChevronRightIcon w={8} h={8} />}
                position="absolute"
                right="8px"
                zIndex="2"
                onClick={(e) => {
                  e.stopPropagation();
                  handleNext();
                }}
                borderRadius="full"
                colorScheme="blackAlpha"
                bg="blackAlpha.600"
                color="white"
                _hover={{ bg: 'blackAlpha.800' }}
              />
            )}

            {/* Thumbnail Tray */}
            {images && images.length > 1 && (
              <HStack
                position="absolute"
                bottom={isFullscreen ? '24px' : '12px'}
                left="50%"
                transform="translateX(-50%)"
                spacing={2}
                zIndex="3"
                bg={isFullscreen ? 'blackAlpha.600' : 'gray.100'}
                p={2}
                borderRadius="lg"
                maxW="90%"
                overflowX="auto"
                justifyContent="center"
                css={{
                  '&::-webkit-scrollbar': { display: 'none' },
                  '-ms-overflow-style': 'none',
                  'scrollbar-width': 'none',
                }}
              >
                {images.map((img, idx) => (
                  <Box
                    key={idx}
                    cursor="pointer"
                    border="2px solid"
                    borderColor={currentIndex === idx ? '#3182ce' : (isFullscreen ? 'whiteAlpha.300' : 'gray.300')}
                    borderRadius="md"
                    overflow="hidden"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentIndex(idx);
                    }}
                    opacity={currentIndex === idx ? 1 : 0.6}
                    _hover={{ opacity: 1 }}
                    transition="all 0.2s"
                  >
                    <Image
                      src={img}
                      w="40px"
                      h="40px"
                      objectFit="cover"
                      alt={`Thumbnail ${idx + 1}`}
                    />
                  </Box>
                ))}
              </HStack>
            )}
          </Box>

          {/* Indicator at the bottom */}
          {images.length > 1 && (
            <Text fontSize="14px" fontWeight="500" color="gray.500" mt={4} fontFamily="Inter">
              {currentIndex + 1} of {images.length}
            </Text>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ShowImgPopup;
